import Debug from 'debug'
import type { Result } from 'neverthrow'
import { err, ok } from 'neverthrow'

import { downloadPathItem } from '@jotta/grpc-connect-client/content'
import { ErrorType } from '@jotta/grpc-web/no/jotta/openapi/error_pb'
import { FileServicePromiseClient } from '@jotta/grpc-web/no/jotta/openapi/file/file.v2_grpc_web_pb'
import type {
  AllocateResponse,
  GetPathRequest,
} from '@jotta/grpc-web/no/jotta/openapi/file/file.v2_pb'
import {
  AllocateConflictHandler,
  AllocateRequest,
  CopyRequest,
  CreateFolderRequest,
  ItemKind,
  ItemType,
  ListRevisionsRequest,
  MoveRequest,
} from '@jotta/grpc-web/no/jotta/openapi/file/file.v2_pb'
import { parseApiErrorResponse } from '@jotta/types/AppError'
import type { PathItemObject } from '@jotta/types/Files'
import { env } from '@jotta/utils/env'
import type { FileMeta } from '@jotta/utils/files'
import { createPath, popPath } from '@jotta/utils/pathUtils'

import { getPath } from './files/getPath'
import { parsePathItem } from './files/parsePathItem'
import { getAuthMeta, getMeta, withGrpcClientMiddleware } from './grpcutils'

export {
  AllocateResponse,
  FileRevision,
  FileState,
  ItemAction,
  ItemKind,
  ItemType,
  ListRevisionsResponse,
  PathItem,
} from '@jotta/grpc-web/no/jotta/openapi/file/file.v2_pb'

export const debug = Debug('jotta:grpc:file')

export type SortDir = keyof typeof GetPathRequest.Sort
let fileServiceClient: FileServicePromiseClient

export function getFileServiceClient() {
  if (!fileServiceClient) {
    fileServiceClient = withGrpcClientMiddleware(
      'file',
      new FileServicePromiseClient(env.grpcApi),
    )
  }
  return fileServiceClient
}

export function isFolder(item: PathItemObject) {
  return item.type === ItemType.FOLDER
}

export async function getDownloadLink({
  path,
  includeDeleted = false,
}: {
  path: string
  includeDeleted?: boolean
}): Promise<string> {
  const { item } = await getPath({
    path,
    includeDeleted,
    includeChildren: false,
    includeThumb: false,
    includeDownloadLinks: true,
  })
  return item.downloadLink
}
export async function downloadItem({
  path,
  includeDeleted = false,
}: {
  path: string
  includeDeleted?: boolean
}) {
  const { item } = await getPath({
    path,
    includeDeleted,
    includeChildren: false,
    includeThumb: false,
    includeDownloadLinks: true,
  })
  return downloadPathItem(item)
}

export async function move(
  oldPath: string,
  newPath: string,
  conflictHandler = MoveRequest.ConflictHandler.CREATE_NEW_NAME,
) {
  const moveRequest = new MoveRequest()
  moveRequest.setFromPath(oldPath)
  moveRequest.setToPath(newPath)
  moveRequest.setConflictHandler(conflictHandler)
  moveRequest.setSupportsBackgroundJobs(true)

  return await getFileServiceClient().move(moveRequest)
}

export async function copy(
  oldPath: string,
  newPath: string,
  conflictHandler = CopyRequest.ConflictHandler.CREATE_NEW_NAME,
) {
  const copyRequest = new CopyRequest()
  copyRequest.setFromPath(oldPath)
  copyRequest.setToPath(newPath)
  copyRequest.setConflictHandler(conflictHandler)
  copyRequest.setSupportsBackgroundJobs(true)

  return await getFileServiceClient().copy(copyRequest)
}

export enum FileTransferConflictHandler {
  UNKNOWN = 0,
  REJECT_CONFLICTS = 1,
  CREATE_NEW_NAME = 2,
  CREATE_NEW_REVISION = 3,
}

export type TransferFileResult = {
  completedSuccessfully: boolean
  backgroundJobId?: string
}

export interface FileTransferInfo {
  transferType: 'COPY' | 'MOVE'
  fileName: string
  error?: unknown
  oldPath: string
  newPath: string
  conflictHandler: FileTransferConflictHandler
  result: TransferFileResult
}

export interface SingeFileTransferParams {
  transferType: 'COPY' | 'MOVE'
  fileName: string
  sourceDirectoryPath: string
  targetDirectoryPath: string
  onFileTransferred?: (options: FileTransferInfo) => Promise<TransferFileResult>
  conflictHandler?: FileTransferConflictHandler
}

export async function transferFile({
  fileName,
  sourceDirectoryPath,
  targetDirectoryPath,
  transferType,
  onFileTransferred,
  conflictHandler = FileTransferConflictHandler.REJECT_CONFLICTS,
}: SingeFileTransferParams): Promise<TransferFileResult> {
  const oldPath = createPath(sourceDirectoryPath, fileName)
  const newPath = createPath(targetDirectoryPath, fileName)
  const transferFn = transferType === 'COPY' ? copy : move
  try {
    const res = await transferFn(oldPath, newPath, conflictHandler as any)
    const result = {
      completedSuccessfully: res.getBackgroundJob() === undefined,
      backgroundJobId: res.getBackgroundJob()?.getId(),
    }
    if (onFileTransferred) {
      return onFileTransferred({
        transferType,
        fileName,
        oldPath: popPath(oldPath),
        newPath: popPath(newPath),
        conflictHandler,
        result,
      })
    }
    return result
  } catch (error) {
    const result = { completedSuccessfully: false }
    if (onFileTransferred) {
      return onFileTransferred({
        transferType,
        fileName,
        oldPath: popPath(oldPath),
        newPath: popPath(newPath),
        error,
        conflictHandler,
        result,
      })
    }
    return result
  }
}

export interface FileTransferParams {
  transferType: 'COPY' | 'MOVE'
  filePaths: string[]
  targetDirectoryPath: string
  onFileTransferred?: (info: FileTransferInfo) => Promise<TransferFileResult>
}

export const NullByteChecksum = 'd41d8cd98f00b204e9800998ecf8427e'
export type AllocateError =
  | 'FILE_ONTO_FILE'
  | 'FILE_ONTO_FOLDER'
  | 'FOLDER_ONTO_FILE'

export interface AllocateParams {
  fileMeta: FileMeta
  apiPath: string
  md5: string
  conflictHandler: AllocateConflictHandler
  token?: string
}

export async function allocate({
  fileMeta,
  apiPath,
  conflictHandler,
  md5,
  token,
}: AllocateParams): Promise<Result<AllocateResponse.AsObject, AllocateError>> {
  const { name, webkitRelativePath, size, lastModified } = fileMeta.file
  const path = createPath(
    apiPath,
    fileMeta.relativePath || webkitRelativePath || name,
  )

  const allocateRequest = new AllocateRequest()
  allocateRequest.setPath(path)
  allocateRequest.setMd5(md5)
  allocateRequest.setSize(size)
  allocateRequest.setCreatedAtMillis(lastModified)
  allocateRequest.setModifiedAtMillis(lastModified)
  allocateRequest.setConflictHandler(conflictHandler)
  const meta = await getAuthMeta(token)
  try {
    const response = await getFileServiceClient().allocate(
      allocateRequest,
      meta,
    )
    const responseObject = response.toObject()
    const { uploadUrl, uploadId } = responseObject
    if (!uploadUrl || !uploadId) {
      throw new Error('Missing upload URL or ID')
    }

    return ok(responseObject)
  } catch (error) {
    const parsedErrorResponse = parseApiErrorResponse(error)
    if (parsedErrorResponse.success) {
      const errorData = parsedErrorResponse.data
      debug('allocate error.errors', errorData)
      const code = errorData.errors.at(0)?.code
      const allocErr: AllocateError | null =
        code === ErrorType.FILE_ONTO_FILE
          ? 'FILE_ONTO_FILE'
          : code === ErrorType.FILE_ONTO_FOLDER
            ? 'FILE_ONTO_FOLDER'
            : code === ErrorType.FOLDER_ONTO_FILE
              ? 'FOLDER_ONTO_FILE'
              : null

      if (allocErr) {
        debug(`allocate ErrorType:${allocErr}`)
        return err(allocErr)
      }
    }

    debug('allocate unhandled error %O', error)

    throw error
  }
}

export async function createFolder({
  path,
  conflictHandler = CreateFolderRequest.ConflictHandler.CREATE_NEW_FOLDER,
}: {
  path: string
  conflictHandler?: CreateFolderRequest.ConflictHandler
}): Promise<PathItemObject> {
  const createFolderRequest = new CreateFolderRequest()
  createFolderRequest.setPath(path)
  createFolderRequest.setConflictHandler(conflictHandler)

  const response =
    await getFileServiceClient().createFolder(createFolderRequest)
  const pathItem = response.getFolder()
  if (!pathItem) {
    throw new Error('pathItem missing from creteFolder response')
  }

  return await parsePathItem({ pathItem })
}

export async function listRevisions(path: string) {
  const req = new ListRevisionsRequest()
  req.setPath(path)

  const response = await getFileServiceClient().listRevisions(req, getMeta())
  return response.toObject().revisionsList
}

export function getEmptyPathItem(): PathItemObject {
  return {
    actionList: [],
    checksum: '',
    childrenList: [],
    commentAuthToken: '',
    commentItemId: '',
    createdAtMillis: Date.now(),
    deletedAtMillis: 0,
    downloadLink: '',
    kind: ItemKind.REGULAR_FOLDER,
    mime: 'application/octet-stream',
    modifiedAtMillis: 0,
    name: '',
    owner: '',
    path: '/',
    publicLinkId: '',
    encodedContentRef: '',
    size: 0,
    thumbLink: '',
    type: ItemType.FOLDER,
    folderShareId: '',
    contentId: '',
    accessTokenOverride: undefined,
    isSearchResult: false,
  }
}
export async function create({ path, name }: { path: string; name: string }) {
  const result = await allocate({
    fileMeta: { file: new File([], name) },
    md5: NullByteChecksum,
    apiPath: path,
    conflictHandler: AllocateConflictHandler.CREATE_NEW_FILE,
  })
  if (result.isOk()) {
    return result._unsafeUnwrap()
  } else {
    throw new Error(result._unsafeUnwrapErr())
  }
}
